/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import * as analytics from 'analytics';
import invariant from 'invariant';
import { getTritonToken } from 'api/juke';
import setCookieSync from 'util/setCookieSync';
import { tritonConsentTag, getEUConsentString } from 'util/consent';
import AudioPlayer from './AudioPlayer';
import { isHuawei } from 'util/device';
import logger from 'util/logger';

class AudioPlayerStation extends AudioPlayer {
  /**
   * Enumeration of station types
   * @static
   * @type {object}
   */
  static stationTypes = {
    TRITON: 'triton',
    GENERIC: 'generic',
  };

  /**
   * Given a station object, builts up an object with URL's from which to fetch audio streams
   * @param {string} playSessionId
   * @param {object} station
   * @returns {object}
   */
  async getSources(playSessionId, station) {
    let queryParams = `?sbmid=${playSessionId}&dist=jukefm&${tritonConsentTag}`;

    if (station.type === AudioPlayerStation.stationTypes.TRITON) {
      const tritonEndpoint = '//playerservices.streamtheworld.com/api/livestream-redirect';

      const consentString = getEUConsentString();
      const gdpr = consentString ? 1 : 0;
      const cs = consentString || '';

      queryParams += `&gdpr=${gdpr}&gdpr_consent=${cs}`;

      if (this.prefersHD) {
        try {
          const token = await getTritonToken();

          if (token && typeof station.tritonMountHD === 'string') {
            queryParams += `&tdtok=${token}`;

            return {
              'audio/mp4': `${tritonEndpoint}/${station.tritonMountHD}.aac${queryParams}`,
            };
          }
        } catch (error) {
          logger.error(error);
        }

        logger.warn('[AudioPlayerStation] Token or HD mount is not available. Falling back to standard mp3.');

        // Gracefully fallback to SD stream
        return {
          'audio/mp3': `${tritonEndpoint}/${station.tritonMount}.mp3${queryParams}`,
        };
      }

      return {
        'audio/mp4': `${tritonEndpoint}/${station.tritonMount}AAC.aac${queryParams}`,
        'audio/mp3': `${tritonEndpoint}/${station.tritonMount}.mp3${queryParams}`,
      };
    }

    if (station.type === AudioPlayerStation.stationTypes.GENERIC) {
      const { audioUrl, audioUrlAac, audioUrlHls } = station;
      const sourceMp4 = audioUrlAac ? { 'audio/mp4': audioUrlAac } : null;
      const sourceMp3 = audioUrl ? { 'audio/mp3': audioUrl } : null;
      const sourceHls = audioUrlHls ? { 'application/x-mpegurl': audioUrlHls } : null;

      return {
        ...sourceMp4,
        ...sourceMp3,
        ...sourceHls,
      };
    }

    throw new Error(`Unknown station type ${station.type}.`);
  }

  /**
   * Loads and initialises the stream source of the current station
   */
  async load() {
    const audioStreamSource = await this.getSources(this.playSessionId, this.station);

    await this.initialisePlayer(audioStreamSource);

    if (this.station.type === AudioPlayerStation.stationTypes.TRITON) {
      // TODO As sooon as the HD mount field is availale use the correct mount
      setCookieSync(this.station.tritonMount);
    }

    const imageUrl = this.station && this.station.image ? this.station.image.url : '';
    this.setMediaSessionData(this.station.name, imageUrl);

    // Huawei QuickApp uses the native android player, if we start it here there are two players active
    if (!isHuawei) {
      await this.audioElement.play();
    }

    analytics.trackPlayLiveAudio(this.station, this.audioElement.currentTime);
  }

  /**
   * Resumes audio playback
   */
  async resume() {
    const audioStreamSource = await this.getSources(this.playSessionId, this.station, this.prefersHD);
    await this.initialisePlayer(audioStreamSource);

    invariant(this.playSessionId && this.station, 'PlaySessionId and Station should both be set.');

    // Huawei QuickApp uses the native android player, if we start it here there are two players active
    if (!isHuawei) {
      await this.audioElement.play();
    }

    analytics.trackResumeLiveAudio(this.station, this.audioElement.currentTime);
    this.healthyPlayDetector.resume();
  }

  /**
   * Plays audio from a given station
   * @param {string} playSessionId
   * @param {object} station
   * @param {Boolean} useHD
   */
  async play(playSessionId, station, useHD = false) {
    // Get the preferance for HD audio
    this.prefersHD = useHD;

    if (this.playSessionId === playSessionId) {
      // Resume playback if it's the same sesseion
      await this.resume();
    } else {
      // Set and load content on new session
      this.playSessionId = playSessionId;
      this.station = station;

      analytics.trackStartLiveAudio(this.station, this.audioElement.currentTime);

      await this.load();
      this.healthyPlayDetector.startNew();
    }
  }

  /**
   * Stops the audio playback
   */
  stop(trackAnalytics = true) {
    super.stop();
    this.audioElement.innerHTML = '';
    this.audioElement.src = '';

    if (trackAnalytics) {
      analytics.stopHeartBeat();
    }

    if (this.station && trackAnalytics) {
      analytics.trackStopLiveAudio(this.station, this.audioElement.currentTime);
    }
  }
}

const audioPlayerStation = new AudioPlayerStation();
export default audioPlayerStation;
